import { filterBy, orderBy } from '@progress/kendo-data-query';
import { Grid, GridCell, GridColumn } from '@progress/kendo-react-grid';
import Body from 'commons/card/Body';
import { default as Card } from 'commons/card/CollapsableCard';
import ButtonCell from 'pages/tables/components/ButtonCell';
import CheckBoxCell from 'pages/tables/components/CheckBoxCell';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'scss/_tablePage.scss';
import * as list from '../../commons/models/list';
import AddParameterModal from '../parameterGestion/modals/AddParameterModal';
import Toolbar from './components/Toolbar';
import { DIFFUSEURS } from 'pages/parameterGestion/constants/types';
import { Pager } from '@progress/kendo-react-data-tools';
import PropTypes from 'prop-types';
import { pubApi, useDeleteURLMutation, usePostUrlMutation, usePutUrlMutation } from '../../redux/api/cache';
import { useDispatch } from 'react-redux';

export default function GestionGrid(props) {
  const [modifyModal, setModifyModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [data, setData] = useState(list.stringify(props.data.data));
  const [filteredData, setFilteredData] = useState(data);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(10);
  const [selectedRowData, setSelectedRowData] = useState(undefined);
  const [sort, setSort] = useState(list.sort(props.data.meta.definition));
  const [meta, setMeta] = useState(props.data.meta);
  const SELECTED_FIELD = 'selected';
  const [postUrl] = usePostUrlMutation();
  const [putUrl] = usePutUrlMutation();
  const [removeUrl] = useDeleteURLMutation();
  const [status, setStatuts] = useState(null);
  const [wrapperGridHeight, setWrapperGridHeight] = useState(0);
  const divRef = useRef(null);
  const dispatch = useDispatch();

  const setHeight = () => {
    if (divRef?.current)
      setWrapperGridHeight(window.innerHeight - divRef?.current.getBoundingClientRect().top);
  };

  const wrapperRef = useCallback((node) => {
    if (node) {
      divRef.current = node;
      setHeight();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", setHeight);
    return () => window.removeEventListener("resize", setHeight);
  }, []);

  const toggleModifyDialog = () => {
    setModifyModal(!modifyModal);
  };

  useEffect(() => {
    setMeta(props.data.meta);
  }, [props.data.meta]);

  useEffect(() => {
    setData(props.data.data);
  }, [props.data.data]);

  useEffect(() => {
    setSort(props.data.meta.definition);
  }, [props.data.meta.definition]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const send = async (data, funcType) => {
    var body = data;
    if (funcType === 'POST') {
      await postUrl({ path: props.path, body });
    } else {
      await putUrl({ path: props.path, body });
    }
    dispatch(pubApi.util.resetApiState());
  };

  const columnNames = useMemo(
    () => list.getColumnNames(meta.definition),
    [meta.definition]
  );

  const gridColumns = useCallback(
    (columnNames) => {
      return columnNames.map((item, i) => {
        return (
          <GridColumn
            {...list.calculateWidth(
              item,
              i === list.getColumnNames(meta.definition).length - 1,
              data,
              true
            )}
            cell={list.isCheckBox(data, item.field) ? CheckBoxCell : list.isBtn(item.field) ? ButtonCell : GridCell}
            field={item.field}
            title={item.label}
            key={i}
          >
            {item.children.length !== 0
              ? gridColumns(item.children)
              : undefined}
          </GridColumn>
        );
      });
    },
    [data, meta.definition]
  );

  const processData = useMemo(() => {
    return orderBy(filteredData, sort)
      .filter(f => {
        if (props.id !== DIFFUSEURS || status === null)
          return true;

        return f.diff_statut === status;
      })
      .slice(skip, take + skip);
  }, [filteredData, sort, props.id, status, skip, take]);

  return (
    <div style={{ marginTop: '0' }}>
      {createModal && (
        <AddParameterModal
          rowData={list.getColumnNamesFromArray(meta.definition)}
          definition={meta.definition}
          title={props.creation}
          name={props.name}
          toggleDialog={() => setCreateModal(!createModal)}
          save={async (data) => send(data, 'POST')}
          firstCol={props.firstCol}
          form={true}
          id={props.id}
        ></AddParameterModal>
      )}
      {modifyModal && (
        <AddParameterModal
          modification={true}
          title={props.modification}
          name={props.name}
          rowData={selectedRowData}
          definition={meta.definition}
          removeRow={async (key, media, diff) => {
            if (media)
              await removeUrl({ path: props.path + `?format=${key}&media=${media}&diff=${diff}` });
            else
              await removeUrl({ path: props.path + `?${props.firstCol}=${key}&` });
            dispatch(pubApi.util.resetApiState());
          }}
          toggleDialog={toggleModifyDialog}
          save={async (data) => send(data, 'PUT')}
          firstCol={props.firstCol}
          form={false}
          id={props.id}
          data={filteredData}
        ></AddParameterModal>
      )}
      <Card type='none' canCollapse={false}>
        <Body>
          {!props.canAdd && (<h3>{`Pour un ajout, vous pouvez vous référer à l'équipe TI.`}</h3>)}
          <Toolbar
            showCreateBtn={props.canAdd}
            txtBtn={props.creation}
            diffuseur={props.id === DIFFUSEURS}
            onCreateClick={() => setCreateModal(!createModal)}
            onSearchChange={(value) => {
              if (data) {
                var filters = list
                  .flatten(props.data.meta.definition)
                  .filter(f => { let d = data[0]; return typeof d[f.key] === 'string'; })
                  .map((item) => {
                    return {
                      field: item.key,
                      operator: 'contains',
                      value: value.target.value,
                      ignoreCase: true,
                    };
                  });
                setFilteredData(
                  filterBy(data, {
                    logic: 'or',
                    filters: filters,
                  })
                );
              }
            }}
            setStatuts={setStatuts}
          ></Toolbar>
          <div style={{ display: 'flex', flexDirection: 'column', height: wrapperGridHeight - 50 }} ref={wrapperRef} >
            <Grid
              selectedField={SELECTED_FIELD}
              onRowClick={(e) => {
                if (props.canAdd) {
                  setSelectedRowData(e.dataItem);
                  toggleModifyDialog();
                }
              }}
              style={{ overflow: 'auto' }}
              data={processData}
              sortable
              resizable
              sort={sort}
              onSortChange={(e) => setSort(e.sort)}
            >
              {gridColumns(columnNames)}
            </Grid>
            <Pager skip={skip} take={take} total={processData.length} buttonCount={10} previousNext={true} pageSizes={[10, 20, 50, 100, 500]} onPageChange={(event) => { setSkip(event.skip); setTake(event.take); }} />
          </div>
        </Body>
      </Card>
    </div >
  );
}

GestionGrid.propTypes = {
  data: PropTypes.any,
  route: PropTypes.string,
  firstCol: PropTypes.string,
  path: PropTypes.string,
  creation: PropTypes.string,
  canAdd: PropTypes.bool,
  modification: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string
};